import { useEffect, useState, useRef, memo } from "react";
import { Box, Button, Stack, IconButton } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload"; // <-- Icono para subir
import CloseIcon from "@mui/icons-material/Close";
import imageCompression from "browser-image-compression";

const PhotoCapture = ({ onPhotosChange, onUploadSubmit, maxPhotos = 4 }) => {
  const [photos, setPhotos] = useState([]);
  const cameraInputReference = useRef(null);
  const galleryInputReference = useRef(null);

  const handleCameraClick = () => cameraInputReference.current.click();
  const handleGalleryClick = () => galleryInputReference.current.click();

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && photos.length < maxPhotos) {
      try {
        const options = {
          maxSizeMB: 0.8,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        };

        const compressedFile = await imageCompression(file, options);

        const newPhoto = {
          file: compressedFile,
          previewUrl: URL.createObjectURL(compressedFile),
        };

        setPhotos((previousPhotos) => {
          const updatedPhotos = [...previousPhotos, newPhoto];
          onPhotosChange(updatedPhotos.map((p) => p.file));
          return updatedPhotos;
        });
      } catch (error) {
        console.error("Image compression error:", error);
      }
    }
    event.target.value = null;
  };

  const handleDeletePhoto = (indexToDelete) => {
    setPhotos((previousPhotos) => {
      const updatedPhotos = previousPhotos.filter(
        (_, index) => index !== indexToDelete,
      );
      onPhotosChange(updatedPhotos.map((p) => p.file));
      return updatedPhotos;
    });
  };

  const isMaxPhotosReached = photos.length >= maxPhotos;

  return (
    <Stack spacing={2} sx={{ marginTop: "0px !important" }}>
      <input
        type="file"
        accept="image/*"
        capture="environment"
        ref={cameraInputReference}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
      <input
        type="file"
        accept="image/*"
        ref={galleryInputReference}
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      {isMaxPhotosReached ? (
        <Button
          variant="contained"
          disabled
          startIcon={<PhotoCameraIcon />}
          sx={{
            py: 1.5,
            borderRadius: "0px !important",
            fontWeight: "Bolder !important",
            backgroundColor: "#F5B01C !important",
          }}
        >
          Máximo de fotos alcanzado
        </Button>
      ) : (
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{ marginTop: "0px !important" }}
        >
          <Button
            variant="contained"
            fullWidth
            startIcon={<PhotoCameraIcon />}
            onClick={handleCameraClick}
            sx={{
              width: "100% !important",
              display: "flex",
              py: 1.5,
              borderRadius: "0px !important",
              backgroundColor: "#F5B01C !important",
              color: "white",
              fontWeight: "Bolder !important",
              "&:hover": { backgroundColor: "#E0A010 !important" },
            }}
          >
            Cámara
          </Button>

          <Button
            variant="contained"
            fullWidth
            startIcon={<PhotoLibraryIcon />}
            onClick={handleGalleryClick}
            sx={{
              width: "100% !important",
              display: "flex",
              py: 1.5,
              borderRadius: "0px !important",
              backgroundColor: "#42a5f5 !important",
              color: "white",
              fontWeight: "Bolder !important",
              "&:hover": { backgroundColor: "#1e88e5 !important" },
            }}
          >
            Galería
          </Button>
        </Stack>
      )}

      <Stack direction="row" spacing={1.5} justifyContent="center">
        {photos.map((photo, index) => (
          <Box key={index} sx={{ position: "relative", width: 60, height: 60 }}>
            <Box
              component="img"
              src={photo.previewUrl}
              alt={`preview ${index}`}
              sx={{
                width: "100%",
                height: "100%",
                borderRadius: 1,
                objectFit: "cover",
              }}
            />
            <IconButton
              size="small"
              onClick={() => handleDeletePhoto(index)}
              sx={{
                position: "absolute",
                top: -10,
                right: -10,
                backgroundColor: "white",
                color: "black",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        {[...Array(maxPhotos - photos.length)].map((_, index) => (
          <Box
            key={`placeholder-${index}`}
            sx={{
              width: 60,
              height: 60,
              backgroundColor: "#D3D3D3",
              borderRadius: 1,
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
};

export default memo(PhotoCapture);
